import SHA256 from 'crypto-js/sha256';
import Strings from '@/constants/String';

/**
 * GA4 for the website — the web counterpart of the app's
 * `services/firebase/analytics.js`, logging into the same property.
 *
 * gtag.js itself is loaded by `index.html`, and only on production hosts; there
 * it also sets `window.BYZ_GA_ENABLED`. Everywhere else these helpers just log to
 * the console, so QA and local sessions never reach the reports.
 *
 * Custom events are prefixed `web_` (the app uses `app_mrkt_`). GA's standard
 * `page_view` and `purchase` are sent unprefixed alongside, because only the
 * exact names fill GA's built-in page and revenue reports.
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    BYZ_GA_ENABLED?: boolean;
  }
}

type Params = Record<string, unknown>;

const { measurementId: MEASUREMENT_ID, eventPrefix: EVENT_PREFIX } = Strings.analytics;
export const AnalyticsEvents = Strings.AnalyticsEvents;

// GA4 caps event names at 40 chars and string params at 100.
const MAX_EVENT_NAME = 40;
const MAX_PARAM_VALUE = 100;

const isEnabled = () => Boolean(window.BYZ_GA_ENABLED && typeof window.gtag === 'function');

const send = (...args: unknown[]) => {
  try {
    if (isEnabled()) window.gtag!(...args);
  } catch (e) {
    console.log('[Analytics] gtag failed', e);
  }
};

const toEventName = (name: string) => {
  const cleaned = String(name)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_');
  const full = cleaned.startsWith(EVENT_PREFIX) ? cleaned : `${EVENT_PREFIX}${cleaned}`;
  return full.slice(0, MAX_EVENT_NAME);
};

/** Drops null/undefined and coerces values into types GA accepts. */
const toParams = (params: Params = {}) =>
  Object.entries(params).reduce<Params>((acc, [key, value]) => {
    if (value === null || value === undefined || value === '') return acc;
    if (typeof value === 'number') acc[key] = value;
    else if (typeof value === 'boolean') acc[key] = value ? 'true' : 'false';
    else acc[key] = String(value).slice(0, MAX_PARAM_VALUE);
    return acc;
  }, {});

export const trackEvent = (name: string, params?: Params) => {
  const eventName = toEventName(name);
  const eventParams = toParams(params);
  if (!isEnabled()) console.log('[Analytics]', eventName, eventParams);
  send('event', eventName, eventParams);
};

/**
 * Route changes in a single-page app are not page loads, so gtag is configured
 * with `send_page_view: false` and every view is sent from here instead.
 */
export const trackPageView = () => {
  const params = {
    page_location: window.location.href,
    page_path: window.location.pathname,
    page_title: document.title,
  };
  send('event', 'page_view', params);
  trackEvent(AnalyticsEvents.PAGE_VIEW, params);
};

export interface PurchaseItem {
  item_id?: string;
  item_name?: string;
  item_brand?: string;
  item_category?: string;
  item_variant?: string;
  price?: number;
  quantity?: number;
}

/** GA's standard `purchase` event, which is what fills the revenue reports. */
export const trackPurchase = ({
  transactionId,
  value,
  currency = 'INR',
  tax,
  coupon,
  items = [],
}: {
  transactionId?: string;
  value: number;
  currency?: string;
  tax?: number;
  coupon?: string;
  items?: PurchaseItem[];
}) => {
  if (!transactionId || typeof value !== 'number' || Number.isNaN(value)) return;
  const params = {
    transaction_id: String(transactionId),
    value,
    currency,
    ...(typeof tax === 'number' && !Number.isNaN(tax) && { tax }),
    ...(coupon && { coupon: String(coupon).slice(0, MAX_PARAM_VALUE) }),
    items: items.map((item) => ({ ...toParams(item as Params), quantity: item.quantity ?? 1 })),
  };
  if (!isEnabled()) console.log('[Analytics] purchase', params);
  send('event', 'purchase', params);
};

/**
 * Google Analytics' terms forbid sending PII such as a raw email address, so
 * the email is normalised and SHA-256 hashed — exactly as the app does, so one
 * person gets the same user_id on web and app.
 */
const hashIdentifier = (value: unknown) => SHA256(String(value).trim().toLowerCase()).toString();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const identifierFor = (user: any) => user?.email || user?.userId || user?.id;

let currentUserId: string | null = null;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const identifyUser = (user: any, extra: Params = {}) => {
  const identifier = identifierFor(user);
  if (!identifier) return;

  const userId = hashIdentifier(identifier);
  send('set', 'user_properties', toParams({ is_logged_in: true, ...extra }));
  if (userId === currentUserId) return;

  currentUserId = userId;
  send('config', MEASUREMENT_ID, { user_id: userId, send_page_view: false });
};

export const clearUser = () => {
  currentUserId = null;
  send('set', 'user_properties', { is_logged_in: 'false', login_method: null });
  send('config', MEASUREMENT_ID, { user_id: null, send_page_view: false });
};

/** Picks the user up again after a reload, before any event of the session. */
export const restoreUser = () => {
  try {
    const stored = sessionStorage.getItem('user');
    if (stored) identifyUser(JSON.parse(stored));
  } catch {
    // A corrupt or blocked sessionStorage just means an anonymous session.
  }
};
